/**
 * The nine intake steps of R5.1 / UI spec §6, as a pure ordered registry.
 *
 * `1 Basics · 2 Timing · 3 Classification · 4 Audience · 5 Field Force ·
 *  6 Milestones · 7 Dependencies · 8 Governance · 9 Validation`
 *
 * Each step carries only its stable id and its label key; the label text lives in
 * the i18next catalogue under `intake.step.*` (R18.9), never here. The step
 * components consume this registry so the stepper, the footer's back/continue
 * bounds and the body switch all read one ordering.
 *
 * The steps are directly clickable with no forced linear progression (UI spec
 * §6), so this registry expresses order for Back/Continue only, not a gate.
 */
export type WizardStepId =
  | 'basics'
  | 'timing'
  | 'classification'
  | 'audience'
  | 'fieldForce'
  | 'milestones'
  | 'dependencies'
  | 'governance'
  | 'validation';

export interface WizardStepDef {
  readonly id: WizardStepId;
  /** The i18next key of the step's short stepper label. */
  readonly labelKey: string;
}

/** The nine steps in their spec order (UI spec §6). */
export const WIZARD_STEPS: readonly WizardStepDef[] = [
  { id: 'basics', labelKey: 'intake.step.basics' },
  { id: 'timing', labelKey: 'intake.step.timing' },
  { id: 'classification', labelKey: 'intake.step.classification' },
  { id: 'audience', labelKey: 'intake.step.audience' },
  { id: 'fieldForce', labelKey: 'intake.step.fieldForce' },
  { id: 'milestones', labelKey: 'intake.step.milestones' },
  { id: 'dependencies', labelKey: 'intake.step.dependencies' },
  { id: 'governance', labelKey: 'intake.step.governance' },
  { id: 'validation', labelKey: 'intake.step.validation' },
] as const;

/** The index of a step in {@link WIZARD_STEPS}, or `-1` if unknown. */
export function stepIndex(id: WizardStepId): number {
  return WIZARD_STEPS.findIndex((step) => step.id === id);
}
